import React, { useState, useCallback, useRef, useEffect, FormEvent } from 'react';
import { useAuth } from '../../hooks/auth';
import { Form, OtpContainer, OtpInput, Button, ErrorText, ResendContainer, Timer } from './styles';
import { getPostLoginHomePath } from '../../utils/canManageSubLogins';

interface IOtpVerificationProps {
  phone: string;
  onSuccess: () => void;
  onNeedsRegistration: (phone: string) => void;
}

const OtpVerification: React.FC<IOtpVerificationProps> = ({
  phone,
  onSuccess,
  onNeedsRegistration
}) => {
  const { verifyPhoneOtp, sendPhoneOtp } = useAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(45);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleOtpChange = (index: number, value: string) => {
    const numericValue = value.replace(/\D/g, '');

    if (numericValue.length <= 1) {
      const newOtp = [...otp];
      newOtp[index] = numericValue;
      setOtp(newOtp);

      if (numericValue && index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      setApiError('');

      const otpCode = otp.join('');

      if (otpCode.length !== 6) {
        setApiError('Digite todos os 6 dígitos');
        setLoading(false);
        return;
      }

      try {
        const result = await verifyPhoneOtp({ phone, otp: otpCode });

        if (result?.requiresPhoneCompletion) {
          onNeedsRegistration(phone);
        } else if (onSuccess) {
          onSuccess();
        } else {
          window.location.href = getPostLoginHomePath(result?.user ?? null);
        }
      } catch (error: any) {
        setApiError(
          error.response?.data?.message || 'Código inválido ou expirado'
        );
      } finally {
        setLoading(false);
      }
    },
    [otp, phone, verifyPhoneOtp, onSuccess, onNeedsRegistration]
  );

  const handleResend = useCallback(async () => {
    setLoading(true);
    try {
      await sendPhoneOtp({ phone });
      setTimeRemaining(45);
      setOtp(['', '', '', '', '', '']);
      setApiError('');
      inputRefs.current[0]?.focus();
    } catch (error: any) {
      setApiError('Erro ao reenviar código');
    } finally {
      setLoading(false);
    }
  }, [phone, sendPhoneOtp]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Form onSubmit={handleSubmit}>
      <h3>Verifique seu WhatsApp</h3>
      <p>Enviamos um código de 6 dígitos para {phone}</p>

      {apiError && <ErrorText>{apiError}</ErrorText>}

      <OtpContainer>
        {otp.map((digit, index) => (
          <OtpInput
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="tel"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            inputMode="numeric"
            disabled={loading}
          />
        ))}
      </OtpContainer>

      <ResendContainer>
        {timeRemaining > 0 ? (
          <Timer>Pode reenviar em: {formatTime(timeRemaining)}</Timer>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={loading}
          >
            Reenviar código
          </button>
        )}
      </ResendContainer>

      {!loading ? (
        <Button type="submit" disabled={otp.join('').length !== 6}>
          VERIFICAR
        </Button>
      ) : (
        <Button type="button" disabled>
          Verificando...
        </Button>
      )}
    </Form>
  );
};

export default OtpVerification;